import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../../context/AppContext';

/* ─── Avatar ──────────────────────────────────────────────── */
export function Avatar({ initials, size = 36, color = '#6C63FF' }) {
  return (
    <div style={{
      width: size, height: size, borderRadius: '50%',
      background: color + '22', color, fontWeight: 700,
      fontSize: size * 0.38, display: 'flex', alignItems: 'center',
      justifyContent: 'center', flexShrink: 0, letterSpacing: '0.02em',
      border: `1.5px solid ${color}44`,
    }}>
      {initials}
    </div>
  );
}

/* ─── Badge ───────────────────────────────────────────────── */
export function Badge({ label, color, bg }) {
  return (
    <span style={{
      display: 'inline-block', fontSize: 11, fontWeight: 600,
      padding: '3px 9px', borderRadius: 6, color, background: bg,
      whiteSpace: 'nowrap',
    }}>
      {label}
    </span>
  );
}

/* ─── NotificationBadge ───────────────────────────────────── */
export function NotificationBadge({ count, dot = false, style = {} }) {
  if (!count || count <= 0) return null;
  if (dot) {
    return (
      <span
        aria-label={`${count} new`}
        style={{
          width: 9, height: 9, borderRadius: '50%', background: '#ef4444',
          display: 'inline-block', boxShadow: '0 0 0 2px #fff', ...style,
        }}
      />
    );
  }
  return (
    <span
      aria-label={`${count} new tasks`}
      style={{
        minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9,
        background: '#ef4444', color: '#fff', fontSize: 11, fontWeight: 700,
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        lineHeight: 1, flexShrink: 0, ...style,
      }}
    >
      {count > 99 ? '99+' : count}
    </span>
  );
}

/* ─── Button ──────────────────────────────────────────────── */
export function Button({ children, variant = 'primary', size = 'md', onClick, disabled, style = {}, type = 'button' }) {
  const sizes = {
    sm: { padding: '6px 14px', fontSize: 13 },
    md: { padding: '10px 20px', fontSize: 14 },
    lg: { padding: '13px 28px', fontSize: 16 },
  };
  const variants = {
    primary: {
      background: '#6C63FF', color: '#fff', border: 'none',
      boxShadow: '0 2px 8px rgba(108,99,255,0.25)',
    },
    secondary: {
      background: '#fff', color: '#374151',
      border: '1.5px solid #e2e8f0', boxShadow: 'none',
    },
    ghost: {
      background: 'transparent', color: '#6C63FF',
      border: '1.5px solid #c4b5fd', boxShadow: 'none',
    },
    danger: {
      background: '#fee2e2', color: '#ef4444',
      border: '1.5px solid #fca5a5', boxShadow: 'none',
    },
  };
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        ...sizes[size], ...variants[variant],
        borderRadius: 10, fontWeight: 600, cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.6 : 1, transition: 'all 0.15s',
        display: 'inline-flex', alignItems: 'center', gap: 6,
        ...style,
      }}
      onMouseEnter={e => { if (!disabled) e.currentTarget.style.filter = 'brightness(0.93)'; }}
      onMouseLeave={e => { e.currentTarget.style.filter = ''; }}
    >
      {children}
    </button>
  );
}

/* ─── Input ───────────────────────────────────────────────── */
export function Input({ label, value, onChange, placeholder, type = 'text', required }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {label && (
        <label style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>
          {label}{required && <span style={{ color: '#ef4444', marginLeft: 3 }}>*</span>}
        </label>
      )}
      <input
        type={type}
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        style={{
          padding: '10px 14px', borderRadius: 10,
          border: '1.5px solid #e2e8f0', fontSize: 14,
          color: '#1e293b', outline: 'none', background: '#fff',
          transition: 'border-color 0.15s',
        }}
        onFocus={e => { e.target.style.borderColor = '#6C63FF'; }}
        onBlur={e => { e.target.style.borderColor = '#e2e8f0'; }}
      />
    </div>
  );
}

/* ─── Textarea ────────────────────────────────────────────── */
export function Textarea({ label, value, onChange, placeholder, rows = 3 }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {label && <label style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{label}</label>}
      <textarea
        value={value}
        onChange={onChange}
        placeholder={placeholder}
        rows={rows}
        style={{
          padding: '10px 14px', borderRadius: 10,
          border: '1.5px solid #e2e8f0', fontSize: 14,
          color: '#1e293b', outline: 'none', background: '#fff',
          resize: 'vertical', transition: 'border-color 0.15s', lineHeight: 1.6,
        }}
        onFocus={e => { e.target.style.borderColor = '#6C63FF'; }}
        onBlur={e => { e.target.style.borderColor = '#e2e8f0'; }}
      />
    </div>
  );
}

/* ─── Select ──────────────────────────────────────────────── */
export function Select({ label, value, onChange, options }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {label && <label style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>{label}</label>}
      <select
        value={value}
        onChange={onChange}
        style={{
          padding: '10px 14px', borderRadius: 10,
          border: '1.5px solid #e2e8f0', fontSize: 14,
          color: '#1e293b', outline: 'none', background: '#fff',
          cursor: 'pointer',
        }}
        onFocus={e => { e.target.style.borderColor = '#6C63FF'; }}
        onBlur={e => { e.target.style.borderColor = '#e2e8f0'; }}
      >
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

/* ─── Modal ───────────────────────────────────────────────── */
export function Modal({ title, children, onClose, maxWidth = 480 }) {
  return (
    <div
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(15,15,26,0.5)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: '1rem', backdropFilter: 'blur(4px)',
        animation: 'fadeIn 0.15s ease',
      }}
    >
      <div style={{
        background: '#fff', borderRadius: 20, padding: '2rem',
        width: '100%', maxWidth, boxShadow: '0 24px 64px rgba(0,0,0,0.2)',
        animation: 'fadeInUp 0.2s ease',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem' }}>
          <h2 style={{ fontSize: 20, fontWeight: 800, color: '#0f0f1a', letterSpacing: '-0.02em' }}>{title}</h2>
          <button
            onClick={onClose}
            style={{
              width: 32, height: 32, borderRadius: 10, border: '1.5px solid #e2e8f0',
              background: '#f8fafc', color: '#64748b', fontSize: 18, lineHeight: 1,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >×</button>
        </div>
        {children}
      </div>
    </div>
  );
}

/* ─── Toast ───────────────────────────────────────────────── */
export function Toast({ message, type = 'info', onClose }) {
  if (!message) return null;

  const styles = {
    success: { bg: '#ecfdf5', border: '#6ee7b7', color: '#047857' },
    error:   { bg: '#fef2f2', border: '#fca5a5', color: '#b91c1c' },
    info:    { bg: '#eff6ff', border: '#93c5fd', color: '#1d4ed8' },
  };
  const s = styles[type] ?? styles.info;

  return (
    <div
      role="status"
      style={{
        position: 'fixed', bottom: 24, right: 24, zIndex: 2000,
        minWidth: 280, maxWidth: 400, padding: '14px 18px',
        background: s.bg, border: `1.5px solid ${s.border}`, color: s.color,
        borderRadius: 14, boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
        fontSize: 14, fontWeight: 600, lineHeight: 1.5,
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
        animation: 'fadeInUp 0.25s ease',
      }}
    >
      <span>{message}</span>
      {onClose && (
        <button
          type="button"
          onClick={onClose}
          aria-label="Dismiss"
          style={{
            background: 'none', border: 'none', color: s.color, cursor: 'pointer',
            fontSize: 18, lineHeight: 1, padding: 0, opacity: 0.7,
          }}
        >×</button>
      )}
    </div>
  );
}

/* ─── ConfirmToast ──────────────────────────────────────────── */
export function ConfirmToast({
  open,
  title = 'Confirm',
  message,
  confirmLabel = 'Yes, delete',
  cancelLabel = 'No, cancel',
  onConfirm,
  onCancel,
  loading = false,
}) {
  if (!open) return null;

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="confirm-toast-title"
      onClick={e => { if (e.target === e.currentTarget && !loading) onCancel(); }}
      style={{
        position: 'fixed', inset: 0, zIndex: 2000,
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
        padding: '1.5rem', pointerEvents: 'auto',
        background: 'rgba(15,15,26,0.35)', backdropFilter: 'blur(3px)',
        animation: 'fadeIn 0.15s ease',
      }}
    >
      <div style={{
        width: '100%', maxWidth: 420, background: '#fff',
        borderRadius: 16, padding: '1.25rem 1.5rem',
        boxShadow: '0 20px 50px rgba(0,0,0,0.18)',
        border: '1.5px solid #f1f5f9',
        animation: 'fadeInUp 0.2s ease',
      }}>
        <p id="confirm-toast-title" style={{ fontSize: 16, fontWeight: 800, color: '#0f0f1a', marginBottom: 8 }}>
          {title}
        </p>
        <p style={{ fontSize: 14, color: '#64748b', lineHeight: 1.55, marginBottom: '1.25rem' }}>
          {message}
        </p>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <Button variant="secondary" onClick={onCancel} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button variant="danger" onClick={onConfirm} disabled={loading}>
            {loading ? 'Deleting…' : confirmLabel}
          </Button>
        </div>
      </div>
    </div>
  );
}

/* ─── EmptyState ──────────────────────────────────────────── */
export function EmptyState({ icon, title, desc, action }) {
  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      justifyContent: 'center', padding: '4rem 2rem', textAlign: 'center',
    }}>
      <div style={{ fontSize: 48, marginBottom: '1rem' }}>{icon}</div>
      <h3 style={{ fontSize: 18, fontWeight: 700, color: '#1e293b', marginBottom: 8 }}>{title}</h3>
      <p style={{ fontSize: 14, color: '#94a3b8', marginBottom: '1.5rem', maxWidth: 280, lineHeight: 1.6 }}>{desc}</p>
      {action}
    </div>
  );
}

/* ─── ActivityToast ───────────────────────────────────────── */
function ActivityToastItem({ toast, onDismiss, onClick }) {
  useEffect(() => {
    const t = setTimeout(() => onDismiss(toast.id), 5000);
    return () => clearTimeout(t);
  }, [toast.id, onDismiss]);

  const color = toast.projectColor || '#6C63FF';

  return (
    <div
      role="status"
      onClick={() => onClick(toast)}
      style={{
        pointerEvents: 'auto', cursor: 'pointer',
        minWidth: 280, maxWidth: 360, padding: '12px 14px',
        background: '#fff', borderRadius: 14, borderLeft: `4px solid ${color}`,
        boxShadow: '0 12px 40px rgba(0,0,0,0.16)',
        display: 'flex', alignItems: 'flex-start', gap: 10,
        animation: 'fadeInUp 0.25s ease',
      }}
    >
      <div style={{
        width: 32, height: 32, borderRadius: 9, flexShrink: 0, background: color + '1a',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.08em', color: '#94a3b8', marginBottom: 2 }}>NEW ACTIVITY</p>
        <p style={{ fontSize: 13, fontWeight: 600, color: '#1e293b', lineHeight: 1.4 }}>{toast.message}</p>
      </div>
      <button
        type="button"
        onClick={(e) => { e.stopPropagation(); onDismiss(toast.id); }}
        aria-label="Dismiss"
        style={{ background: 'none', border: 'none', color: '#cbd5e1', cursor: 'pointer', fontSize: 16, lineHeight: 1, padding: 2 }}
      >×</button>
    </div>
  );
}

export function ActivityToast() {
  const { activityToasts, dismissActivityToast } = useApp();
  const navigate = useNavigate();

  if (!activityToasts || activityToasts.length === 0) return null;

  // Show at most the latest few so the stack never overflows the screen.
  const visible = activityToasts.slice(-4);

  const handleClick = (toast) => {
    dismissActivityToast(toast.id);
    if (toast.projectId) navigate(`/project/${toast.projectId}`);
  };

  return (
    <div style={{
      position: 'fixed', top: 20, right: 20, zIndex: 2100,
      display: 'flex', flexDirection: 'column', gap: 10, pointerEvents: 'none',
    }}>
      {visible.map(t => (
        <ActivityToastItem key={t.id} toast={t} onDismiss={dismissActivityToast} onClick={handleClick} />
      ))}
    </div>
  );
}
